/**
 * Gate Pass domain logic — pure, and deliberately import-free beyond two other
 * pure modules (constants/gatePass, utils/inventoryRoles). Anything that reaches
 * api/inventory-request pulls in axios's ESM build and cannot be tested here.
 *
 * NO STOCK RULE LIVES HERE ANY MORE. A gate pass never posts and never touches
 * stock — not returnable, not non-returnable, not in either direction. It is a
 * movement record and a printable document, so there is nothing for the UI to
 * warn about before a save. `documentAffectsStock` / `affectsStock` are gone;
 * do not reintroduce them to "tell the user what will happen to stock", because
 * the answer is always nothing.
 *
 * What this file still owns is the custody register (returned vs pending,
 * ageing, overdue) and the action gating.
 */
import { GATE_PASS_STATUS, GATE_PASS_STAGES } from '../constants/gatePass';
import { INV_ACTIONS } from './inventoryRoles';

const S = GATE_PASS_STATUS;

function norm(value) {
  return String(value || '').trim().toUpperCase();
}

export function statusOf(gatePass) {
  return norm(gatePass?.status);
}

function directionOf(gatePass) {
  return norm(gatePass?.direction);
}

export function isReturnable(gatePass) {
  return norm(gatePass?.returnable_flag) === 'RETURNABLE';
}

export function isOutward(gatePass) {
  return directionOf(gatePass) === 'OUTWARD';
}

/* ------------------------------------------------------------------ */
/* Returnable custody tracking                                         */
/* ------------------------------------------------------------------ */

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Quantity still outside on this line. Never negative — an over-return is a server error, not a display one. */
export function pendingReturnQty(line) {
  return Math.max(0, toNumber(line?.quantity) - toNumber(line?.returned_quantity));
}

export function returnProgress(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const total = list.reduce((sum, line) => sum + toNumber(line?.quantity), 0);
  const returned = list.reduce((sum, line) => sum + toNumber(line?.returned_quantity), 0);
  const pct = total > 0 ? Math.min(100, Math.round((returned / total) * 100)) : 0;
  return { returned, total, pending: Math.max(0, total - returned), pct };
}

/** 'YYYY-MM-DD' out of either a date or a full ISO timestamp. */
function toDateOnly(value) {
  const text = String(value || '').trim();
  return text ? text.slice(0, 10) : '';
}

export function todayIso(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function daysBetween(fromIso, toIso) {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return Math.round((to - from) / 86400000);
}

/** Statuses in which a returnable pass is still outside and owes a return. */
const GATE_PASS_OUTSTANDING_STATUSES = [S.GATE_OUT, S.PARTIALLY_RETURNED];

export function isOutstanding(gatePass) {
  return (
    isReturnable(gatePass)
    && isOutward(gatePass)
    && GATE_PASS_OUTSTANDING_STATUSES.includes(statusOf(gatePass))
  );
}

/**
 * Overdue only once the expected date has PASSED. Due today is not overdue —
 * flagging someone red on the morning the item is still due is how a dashboard
 * teaches people to ignore it.
 *
 * The server sends `is_overdue` on every gate-pass payload (list, outstanding,
 * pending-approval and detail all run through the same response builder), and its
 * clock is the authoritative one — a browser in another timezone, or with a wrong
 * system date, must not disagree with the register. The local computation is the
 * fallback for payloads that predate the field or are built client-side.
 */
export function isOverdue(gatePass, today = todayIso()) {
  if (typeof gatePass?.is_overdue === 'boolean') return gatePass.is_overdue;
  if (!isOutstanding(gatePass)) return false;
  const expected = toDateOnly(gatePass?.expected_return_date);
  if (!expected) return false;
  return expected < toDateOnly(today);
}

/** Whole days since the material physically left. Null when it has not left yet. */
export function daysOut(gatePass, today = todayIso()) {
  if (gatePass?.days_out != null) return gatePass.days_out;
  const out = toDateOnly(gatePass?.gate_out_at);
  if (!out) return null;
  return daysBetween(out, toDateOnly(today));
}

/** Whole days past the expected return date, or 0 when not overdue. */
export function daysOverdue(gatePass, today = todayIso()) {
  if (!isOverdue(gatePass, today)) return 0;
  return daysBetween(toDateOnly(gatePass.expected_return_date), toDateOnly(today)) || 0;
}

/* ------------------------------------------------------------------ */
/* Lifecycle                                                           */
/* ------------------------------------------------------------------ */

/**
 * Which stage this pass is waiting at, or null when nobody owes it an action.
 *
 * SECURITY is not an approval — it is the physical gate-out — but it is the
 * stage a security user's queue is keyed on, so it belongs in the same answer.
 * Screens must read this rather than testing for PENDING_STORE directly, so
 * switching the HOD stage on never means touching JSX.
 */
export function resolveNextStage(status) {
  switch (norm(status)) {
    case S.PENDING_DEPARTMENT: return GATE_PASS_STAGES.DEPARTMENT;
    case S.PENDING_STORE: return GATE_PASS_STAGES.STORE;
    case S.APPROVED: return GATE_PASS_STAGES.SECURITY;
    default: return null;
  }
}

/**
 * The stage pending on a gate pass, preferring the server's own answer.
 *
 * Every payload carries `pending_stage`, derived from the microservice's
 * GATE_PASS_APPROVAL_STAGES tuple. Reading it means the day the HOD stage is
 * switched on in that tuple, this frontend follows without a release — which is
 * the whole point of shipping PENDING_DEPARTMENT unreached. resolveNextStage is
 * the fallback, and the two must agree.
 *
 * Screens must call this, never test for PENDING_STORE directly.
 */
export function pendingStageOf(gatePass) {
  const fromServer = norm(gatePass?.pending_stage);
  if (fromServer) return fromServer;
  return resolveNextStage(gatePass?.status);
}

/** Editable / cancellable only while the material is still inside the building. */
const GATE_PASS_PRE_EXIT_STATUSES = [
  S.DRAFT,
  S.PENDING_DEPARTMENT,
  S.PENDING_STORE,
  S.APPROVED,
];

export const GATE_PASS_ACTIONS = {
  EDIT: 'edit',
  SUBMIT: 'submit',
  CANCEL: 'cancel',
  APPROVE: 'approve',
  REJECT: 'reject',
  GATE_OUT: 'gate-out',
  APPROVE_AND_GATE_OUT: 'approve-and-gate-out',
  GATE_IN: 'gate-in',
  RECORD_RETURN: 'record-return',
  CLOSE_NOT_RETURNED: 'close-not-returned',
  PRINT: 'print',
};

const A = GATE_PASS_ACTIONS;

const STAGE_PERMISSION = {
  [GATE_PASS_STAGES.DEPARTMENT]: INV_ACTIONS.GATEPASS_APPROVE_DEPARTMENT,
  [GATE_PASS_STAGES.STORE]: INV_ACTIONS.GATEPASS_APPROVE_STORE,
};

/**
 * Every action this user may take on this pass, as a Set of GATE_PASS_ACTIONS keys.
 *
 * Permission-filtered here rather than at each call site, so a button the backend
 * would reject is never rendered in the first place.
 */
export function getGatePassActions(gatePass, can) {
  const actions = new Set();
  const status = statusOf(gatePass);
  if (!status || typeof can !== 'function') return actions;

  const outward = isOutward(gatePass);
  const stage = pendingStageOf(gatePass);

  if (can(INV_ACTIONS.GATEPASS_PRINT)) actions.add(A.PRINT);

  if (status === S.DRAFT) {
    if (can(INV_ACTIONS.GATEPASS_EDIT)) actions.add(A.EDIT);
    // An inward pass has no approval chain — material arriving needs no permission
    // to arrive, so DRAFT goes straight to the gate.
    if (outward) {
      if (can(INV_ACTIONS.GATEPASS_SUBMIT)) actions.add(A.SUBMIT);
    } else if (can(INV_ACTIONS.GATEPASS_GATEIN)) {
      actions.add(A.GATE_IN);
    }
  }

  const stagePermission = STAGE_PERMISSION[stage];
  if (stagePermission && can(stagePermission)) {
    actions.add(A.APPROVE);
    actions.add(A.REJECT);
    // Phase-1 convenience: the Store Keeper holds both permissions today, so
    // approve-then-gate-out is two clicks with no control value between them.
    // The day Security is a separate login this drops out on its own.
    if (outward && stage === GATE_PASS_STAGES.STORE && can(INV_ACTIONS.GATEPASS_GATEOUT)) {
      actions.add(A.APPROVE_AND_GATE_OUT);
    }
  }

  if (stage === GATE_PASS_STAGES.SECURITY && outward && can(INV_ACTIONS.GATEPASS_GATEOUT)) {
    actions.add(A.GATE_OUT);
  }

  if (GATE_PASS_PRE_EXIT_STATUSES.includes(status) && can(INV_ACTIONS.GATEPASS_CANCEL)) {
    actions.add(A.CANCEL);
  }

  if (isOutstanding(gatePass)) {
    if (can(INV_ACTIONS.GATEPASS_GATEIN)) actions.add(A.RECORD_RETURN);
    if (can(INV_ACTIONS.GATEPASS_CLOSE_NOT_RETURNED)) actions.add(A.CLOSE_NOT_RETURNED);
  }

  return actions;
}
